import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Camera, LogOut, Save, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import NavigationMenu from '../components/NavigationMenu';
import FamilySection from '../components/FamilySection';
import FamilyPlanManager from '../components/FamilyPlanManager';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuth();
  const { accessibilityMode, toggleAccessibility } = useAccessibility();
  const [name, setName] = useState(user?.name || '');
  const [updating, setUpdating] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados para alteração de senha
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Estados para deletar conta
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const resizeImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          const resizedBase64 = canvas.toDataURL('image/jpeg', quality);
          console.log('🖼️ Imagem redimensionada:', width, 'x', height, 'Tamanho:', resizedBase64.length, 'caracteres');
          resolve(resizedBase64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('📸 Arquivo selecionado:', file.name, file.size, 'bytes');
      
      try {
        setUpdating(true);
        
        // Resize and compress image
        const resizedBase64 = await resizeImage(file, 400, 400, 0.85);
        
        console.log('📤 Enviando foto redimensionada para backend...');
        
        // Update profile with new photo
        const response = await updateProfile({ picture: resizedBase64 });
        
        console.log('✅ Resposta do backend:', response);
        
        if (response && response.user) {
          console.log('✅ Foto de perfil atualizada no backend');
        }
        
        toast.success(t('profile.changesSaved'));
        
        // Clear input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('❌ Error uploading photo:', error);
        console.error('❌ Error details:', error.response?.data);
        toast.error(error.response?.data?.detail || t('profile.errorSaving'));
      } finally {
        setUpdating(false);
        console.log('🏁 Upload finalizado');
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t('auth.loginSuccess'));
    navigate('/login');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error(t('profile.errorSaving'));
      return;
    }

    setUpdating(true);
    
    try {
      await updateProfile({ name });
      toast.success(t('profile.changesSaved'));
    } catch (error) {
      console.error('Update error:', error);
      toast.error(t('profile.errorSaving'));
    } finally {
      setUpdating(false);
    }
  };
  
  const handleChangePassword = async () => {
    // Validações
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error(t('profile.fillAllFields'));
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error(t('profile.passwordMinLength'));
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('profile.passwordsDoNotMatch'));
      return;
    }
    
    try {
      setUpdating(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`,
        {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        },
        { withCredentials: true }
      );
      
      toast.success(t('profile.passwordChanged'));
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || t('profile.errorChangingPassword');
      toast.error(message);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'Deletar') {
      toast.error(t('profile.confirmDelete'));
      return;
    }
    
    try {
      setUpdating(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/delete-account`,
        { withCredentials: true }
      );
      
      toast.success(t('profile.accountDeleted'));
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error(t('profile.errorDeletingAccount'));
    } finally {
      setUpdating(false);
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-amber-900 mb-8">{t('profile.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card className="lg:col-span-1 p-6 border-2 border-amber-100 text-center">
            <div className="flex flex-col items-center">
              {/* Avatar */}
              <div className="relative mb-4">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-orange-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center border-4 border-orange-200">
                    <User className="w-16 h-16 text-white" />
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={updating}
                  className="absolute bottom-0 right-0 bg-orange-600 hover:bg-orange-700 text-white rounded-full p-2 shadow-lg transition-colors disabled:opacity-50"
                  title={t('profile.changePhoto')}
                >
                  {updating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-1">{user?.name}</h2>
              <p className="text-sm text-gray-600 mb-4">{user?.email}</p>

              {user?.auth_provider && (
                <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {t('profile.connectedVia')} {user.auth_provider === 'google' ? 'Google' : user.auth_provider}
                </div>
              )}
            </div>
          </Card>

          {/* Edit Profile Card */}
          <Card className="lg:col-span-2 p-6 border-2 border-amber-100">
            <h3 className="text-2xl font-semibold text-amber-900 mb-6">{t('profile.personalInfo')}</h3>

            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.name')}</label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('auth.name')}
                  className="w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
                <Input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">{t('profile.emailCannotBeChanged')}</p>
              </div>

              {/* Modo Vovó Toggle */}
              <div className="p-6 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl border-2 border-pink-300 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white rounded-xl shadow-md">
                      <img src="/vovo-icon.png" alt="Modo Vovó" className="w-10 h-10 object-contain" />
                    </div>
                    <div>
                      <Label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        {t('profile.accessibilityMode')}
                        {accessibilityMode && (
                          <span className="text-xs bg-pink-600 text-white px-2 py-1 rounded-full">{t('profile.accessibilityOn')}</span>
                        )}
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">{t('profile.accessibilityDescription')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={accessibilityMode}
                    onCheckedChange={toggleAccessibility}
                    className="data-[state=checked]:bg-pink-600"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={updating}
                  className="w-full sm:flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('profile.saveChanges')}
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full sm:w-auto border-red-300 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </Button>
                
                <Button
                  type="button"
                  onClick={() => navigate('/subscription/manage')}
                  variant="outline"
                  className="w-full sm:w-auto border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {t('profile.manageSubscription')}
                </Button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <Button
                  type="button"
                  onClick={() => setShowDeleteDialog(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-red-500 text-red-600 hover:bg-red-50"
                >
                  🗑️ {t('profile.deleteAccount')}
                </Button>
                
                {/* Botão Alterar Senha - Apenas para usuários email (não OAuth) */}
                {user?.auth_provider === 'email' && (
                  <Button
                    type="button"
                    onClick={() => setShowPasswordDialog(true)}
                    variant="outline"
                    className="w-full sm:w-auto border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    🔑 {t('profile.changePassword')}
                  </Button>
                )}
              </div>
            </form>
          </Card>
        </div>

        {/* Dialog Alterar Senha */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('profile.changePasswordTitle')}</DialogTitle>
              <DialogDescription>
                {t('profile.changePasswordDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('profile.currentPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  placeholder={t('profile.currentPasswordPlaceholder')}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('profile.newPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  placeholder={t('profile.newPasswordPlaceholder')}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{t('profile.confirmNewPassword')}</Label>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  placeholder={t('profile.confirmPasswordPlaceholder')}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleChangePassword} disabled={updating}>
                {updating ? t('profile.changing') : t('profile.changePassword')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Family Plan Manager - Shows only for Family Plan owners */}


        {/* Dialog Deletar Conta */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">⚠️ {t('profile.deleteAccount')}</DialogTitle>
              <DialogDescription>
                {t('profile.deleteAccountWarning')}. {t('profile.allDataWillBeDeleted')}:
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <ul className="text-sm text-gray-700 space-y-1">
                <li>✗ {t('profile.allRecipes')}</li>
                <li>✗ {t('profile.conversationHistory')}</li>
                <li>✗ {t('profile.mealPlans')}</li>
                <li>✗ {t('profile.subscriptionData')}</li>
                <li>✗ {t('profile.settings')}</li>
              </ul>
              
              <div className="border-t pt-3 mt-3">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('profile.typeDeleteToConfirm')}
                </Label>
                <Input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('profile.deletePlaceholder')}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleDeleteAccount} 
                disabled={updating || deleteConfirmText !== 'Deletar'}
                variant="destructive"
              >
                {updating ? t('profile.deleting') : t('profile.deleteAccountPermanently')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="mt-6">
          <FamilyPlanManager />
        </div>

        {/* Family Section */}
        <div className="mt-6">
          <FamilySection />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
